/**
 * Centralized order cancellation utilities
 * Handles both local and router order cancellation with symbol relationship awareness
 */

import { router } from "../data/Services/ordersRouter.jsx";

/**
 * Cancel all local orders for all symbols
 * Used by global cancellation operations (OpenOrdersPanel, PositionsPanel global close)
 */
export const cancelAllLocalOrders = (
  ordersBySymbol,
  removeOrder,
  overlayOrders = null,
  removeFromOverlay = null,
) => {
  console.log("🧹 Starting global local order cancellation...");
  let totalLocalOrdersRemoved = 0;

  // Method 1: Remove via overlay if available (handles symbol relationships automatically)
  if (overlayOrders && removeFromOverlay) {
    const localOverlayOrders = overlayOrders.filter(
      (order) => order.state === "local",
    );
    console.log(
      `🔍 Found ${localOverlayOrders.length} local orders in overlay:`,
      localOverlayOrders.map((o) => ({
        id: o.id,
        symbol: o.symbol,
        state: o.state,
      })),
    );

    if (localOverlayOrders.length > 0) {
      console.log(
        `🧹 Removing ${localOverlayOrders.length} local orders using overlay`,
      );
      localOverlayOrders.forEach((order) => {
        console.log(`🗑️ Removing order ${order.id} using overlay removeOrder`);
        removeFromOverlay(order.id);
        totalLocalOrdersRemoved++;
      });
    }
  }

  // Method 2: Remove via store for all symbols (complementary cleanup)
  Object.entries(ordersBySymbol).forEach(([symbol, orders]) => {
    const localOrders = orders.filter((order) => order.state === "local");
    console.log(
      `🔍 Found ${localOrders.length} local orders in symbol ${symbol}`,
    );

    localOrders.forEach((order) => {
      console.log(`🗑️ Removing local order ${order.id} from symbol ${symbol}`);
      removeOrder(symbol, order.id);
      totalLocalOrdersRemoved++;
    });
  });

  if (totalLocalOrdersRemoved > 0) {
    console.log(
      `✅ Total de ${totalLocalOrdersRemoved} ordens locais removidas de todos os símbolos`,
    );
  } else {
    console.log(`ℹ️ Nenhuma ordem local encontrada para remoção`);
  }

  return totalLocalOrdersRemoved;
};

/**
 * Cancel local orders for a specific symbol (and related symbols)
 * Used by symbol-specific operations (RouterOperationsSidebar, individual position close)
 */
export const cancelLocalOrdersForSymbol = (
  currentSymbol,
  ordersBySymbol,
  removeOrder,
  overlayOrders = null,
  removeFromOverlay = null,
  getOrdersForSymbol = null,
) => {
  console.log(
    `🧹 Starting local order cancellation for symbol ${currentSymbol}...`,
  );
  let totalLocalOrdersRemoved = 0;

  // Method 1: Remove via overlay if available (handles symbol relationships automatically)
  if (overlayOrders && removeFromOverlay) {
    const localOverlayOrders = overlayOrders.filter(
      (order) => order.state === "local",
    );
    console.log(
      `🔍 Found ${localOverlayOrders.length} local orders in overlay:`,
      localOverlayOrders.map((o) => ({
        id: o.id,
        symbol: o.symbol,
        state: o.state,
      })),
    );

    if (localOverlayOrders.length > 0) {
      console.log(
        `🧹 Removing ${localOverlayOrders.length} local orders using overlay`,
      );
      localOverlayOrders.forEach((order) => {
        console.log(`🗑️ Removing order ${order.id} using overlay removeOrder`);
        removeFromOverlay(order.id);
        totalLocalOrdersRemoved++;
      });
    }
  }

  // Method 2: Handle symbol relationships manually for store-level removal
  const symbolsToCheck = new Set([currentSymbol]);

  // Find related symbols (same 3-letter prefix for futures)
  Object.keys(ordersBySymbol).forEach((storeSymbol) => {
    const currentPrefix = currentSymbol.substring(0, 3);
    const storePrefix = storeSymbol.substring(0, 3);

    if (currentPrefix === storePrefix) {
      symbolsToCheck.add(storeSymbol);
      console.log(
        `✅ Added related symbol: ${storeSymbol} (same prefix: ${currentPrefix})`,
      );
    }
  });

  console.log(
    `🔍 Final symbols to check for local orders:`,
    Array.from(symbolsToCheck),
  );

  // Remove from all related symbols
  symbolsToCheck.forEach((symbolToCheck) => {
    const currentOrders = getOrdersForSymbol
      ? getOrdersForSymbol(symbolToCheck)
      : ordersBySymbol[symbolToCheck] || [];
    const localOrders = currentOrders.filter(
      (order) => order.state === "local",
    );

    console.log(
      `🔍 Found ${localOrders.length} local orders in ${symbolToCheck}:`,
      localOrders.map((o) => ({ id: o.id, symbol: o.symbol, state: o.state })),
    );

    if (localOrders.length > 0) {
      console.log(
        `🧹 Removing ${localOrders.length} local orders from ${symbolToCheck}`,
      );
      localOrders.forEach((order) => {
        console.log(
          `🗑️ Removing order ${order.id} from symbol ${symbolToCheck}`,
        );
        removeOrder(symbolToCheck, order.id);
        totalLocalOrdersRemoved++;
      });
    }
  });

  console.log(
    `✅ Total de ${totalLocalOrdersRemoved} ordens locais removidas para ${currentSymbol}`,
  );
  return totalLocalOrdersRemoved;
};

/**
 * Cancel all router orders (global)
 */
export const cancelAllRouterOrders = async () => {
  console.log("📞 Calling router.cancelAllOrders()");
  await router.cancelAllOrders();
  console.log(
    "✅ Comando cancelAllOrders enviado com sucesso para ordens do router",
  );
};

/**
 * Cancel router orders for specific symbol
 */
export const cancelRouterOrdersForSymbol = async (symbol) => {
  console.log(`📞 Calling router.cancelAllOrdersForSymbol(${symbol})`);
  await router.cancelAllOrdersForSymbol(symbol);
  console.log(`✅ Comando cancelAllOrdersForSymbol enviado para ${symbol}`);
};

/**
 * Close all positions (global)
 */
export const closeAllPositions = async () => {
  console.log("📞 Calling router.releaseAllPositions()");
  await router.releaseAllPositions();
  console.log("✅ Todas as posições fechadas com sucesso");
};

/**
 * Fecha a posição de um símbolo.
 *
 * NÃO verifica o resultado aqui. O desfecho é relatado pelo roteador — `PositionTerminated`,
 * `PositionTerminationFailed`, `Timeout` e `CancelRejected` chegam por `Info.Code` e são
 * tratados em `receiveInfo` (routerConnection.js), com toast e log por estado.
 *
 * Antes esta função dormia 4s e concluía "não fechou" se ENCONTRASSE posição no símbolo — um
 * palpite que não distingue "a posição antiga não fechou" de "o trader abriu posição nova nesse
 * intervalo". Era falso positivo garantido em quem zera e reentra rápido (OrdersRouter#8), e ao
 * mesmo tempo silenciava os avisos reais, que já vinham prontos do roteador e eram descartados.
 */
export const closePositionsForSymbol = async (symbol) => {
  console.log(`📞 Calling router.releaseAllPositionsForSymbol(${symbol})`);
  const result = await router.releaseAllPositionsForSymbol(symbol);
  console.log(`✅ Comando releaseAllPositionsForSymbol enviado:`, result);
  return result;
};
